import { computed } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints.js";
import { toBoolean } from "#ce/utils/props";

/** @typedef {import("../../SpDialog/props.js").SpDialogProps} SpDialogProps */

/**
 * Picks the surface sp-modal renders on for the current breakpoint: an
 * `sp-bottom-sheet` on small screens, an `sp-dialog` otherwise. The
 * `no-bottom-sheet` prop opts out of the sheet entirely (always the dialog).
 * Only one surface is ever in the DOM; crossing the breakpoint swaps it.
 *
 * @param {SpDialogProps & { noBottomSheet?: Boolean|String }} props - The sp-modal props.
 * @returns {{
 *   showBottomSheet: import("vue").ComputedRef<boolean>,
 *   surfaceComponent: import("vue").ComputedRef<"sp-bottom-sheet" | "sp-dialog">,
 *   screenModifiers: import("vue").ComputedRef<string[]>,
 * }}
 */
export function useResponsiveSurface(props) {
  const { isGtSmScreen, gtModifiers } = useBreakpoints();

  const showBottomSheet = computed(() => !toBoolean(props.noBottomSheet) && !isGtSmScreen.value);
  const surfaceComponent = computed(() => (showBottomSheet.value ? "sp-bottom-sheet" : "sp-dialog"));

  return { showBottomSheet, surfaceComponent, screenModifiers: gtModifiers };
}
